import { action, computed, makeObservable, observable, override } from 'mobx';
import { UserSessionContext, ViewModel, asApiError } from '../../../extViewmodels';
import { ILeadFollowUp } from '../queue';
import { ILeadSearchView, ILeadView, ILeadsSearchRequest, LeadAction } from './interfaces';
import { LeadViewModel } from './lead';

/**
 * The LeadsViewModel will load and hold the current lead that is being displayed. It should be used to control what
 * lead is shown at any given time, but not information about the specific lead itself.
 */
export class LeadsViewModel extends ViewModel {
	private mLead: LeadViewModel = null;
	private mAction: LeadAction;
	private lastSuccessfulRequest: ILeadsSearchRequest;
	private nextRequest: ILeadsSearchRequest;

	constructor(userSession?: UserSessionContext) {
		super(userSession);

		makeObservable<LeadsViewModel, 'mLead' | 'mAction' | 'lastSuccessfulRequest' | 'nextRequest'>(this, {
			mLead: observable,
			mAction: observable,
			lastSuccessfulRequest: observable.ref,
			nextRequest: observable.ref,
			action: computed,
			isBusy: override,
			isSearching: computed,
			lead: computed,
			getLeadById: action,
			getNext: action,
			loadPracticeLead: action,
			reloadLead: action,
			reset: action,
			setLeadFollowUp: action,
			setAction: action,
		});
	}

	get action() {
		return this.mAction;
	}

	get isBusy() {
		return this.busy;
	}

	public get isSearching() {
		const leadsSearchRequest = this.nextRequest || this.lastSuccessfulRequest;
		if (leadsSearchRequest) {
			return !!leadsSearchRequest.searches && leadsSearchRequest.searches.some(x => !!x.value);
		}
		return false;
	}

	get lead() {
		return this.mLead;
	}

	public getLeadById = async (id: string) => {
		if (this.busy) {
			return null;
		}

		this.busy = true;

		const result = await this.userSession.webServiceHelper.callWebServiceAsync<ILeadView>(
			this.composeApiUrl({ urlPath: `lead/${id}` }),
			'GET'
		);

		if (result.success) {
			this.mLead = new LeadViewModel(this.userSession, result.value);
			this.busy = false;
			return result.value;
		}

		this.busy = false;
		throw asApiError(result);
	};

	public loadPracticeLead = (leadData: ILeadView) => {
		// Load practice lead data directly without API call (data already fetched)
		this.mLead = new LeadViewModel(this.userSession, leadData);
		return leadData;
	};

	public getNext = async (params?: { ruleId?: string }) => {
		const ruleId = params?.ruleId;

		if (this.busy) {
			return null;
		}

		this.reset();
		this.busy = true;

		const result = await this.userSession.webServiceHelper.callWebServiceAsync<ILeadView>(
			this.composeApiUrl({ queryParams: { ruleId }, urlPath: 'lead' }),
			'GET'
		);

		if (result.success) {
			this.mLead = new LeadViewModel(this.userSession, result.value);
			this.busy = false;
			return result.value;
		}

		this.busy = false;
		if (result.systemCode !== 404) {
			throw asApiError(result);
		}

		return null;
	};

	public reloadLead = async () => {
		if (!this.busy) {
			this.busy = true;

			const result = await this.userSession.webServiceHelper.callWebServiceAsync<ILeadView>(
				this.composeApiUrl({ urlPath: `lead/${this.lead.company.id}` }),
				'GET'
			);

			if (result.success) {
				this.mLead = new LeadViewModel(this.userSession, result.value);
				this.busy = false;
			} else {
				this.busy = false;
				throw asApiError(result);
			}
		}
	};

	public reset = () => {
		this.mAction = null;
		this.busy = false;
		this.loading = false;

		this.mLead = null;
	};

	public setLeadFollowUp = (leadFollowUp: ILeadFollowUp) => {
		this.mLead.setFollowUp(leadFollowUp);
	};

	public setAction = (leadAction: LeadAction) => {
		this.mAction = leadAction;
	};
}

export class LeadSearchResultsViewModel extends ViewModel {
	private mLeads: ILeadSearchView[] = [];
	private searchCounter = 0;

	constructor(userSession: UserSessionContext) {
		super(userSession);

		makeObservable<LeadSearchResultsViewModel, 'mLeads'>(this, {
			mLeads: observable,
			leads: computed,
			reset: action,
			search: action,
		});
	}

	get leads() {
		return this.mLeads;
	}

	public reset = () => {
		this.busy = false;
		this.mLeads = [];
	};

	public search = async (query: string) => {
		this.busy = true;
		const thisRequest = this.searchCounter + 1;
		this.searchCounter += 1;
		const result = await this.userSession.webServiceHelper.callWebServiceAsync<ILeadSearchView[]>(
			this.composeApiUrl({ queryParams: { search: query }, urlPath: `lead/search` }),
			'GET'
		);

		if (this.searchCounter !== thisRequest) {
			return; // stale info
		}

		if (!result.success) {
			this.busy = false;
			throw asApiError(result);
		}

		this.busy = false;

		this.mLeads = result.value;
	};
}
